/**
 * Router Configuration
 *
 * Defines the application routes and their associated guards.
 *
 * Route Structure:
 * - / (Landing) - Public, no guard
 * - /users - AuthGuard + AdminOrStaffGuard
 * - /profile - AuthGuard only
 * - /no-access - AuthGuard only
 * - /error - Public, no guard
 * - * (catch-all) - Redirects to landing page
 *
 * @module routes/router
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthGuard, AdminOrStaffGuard } from './guards';
import {
  LandingPage,
  UsersPage,
  ProfilePage,
  NoAccessPage,
  ErrorPage,
} from '../pages';
import { ROUTES } from './constants';

// =============================================================================
// Router Component
// =============================================================================

/**
 * AppRouter - Main router component with all application routes.
 *
 * Renders the Routes configuration with appropriate guards applied.
 *
 * Guard Application:
 * - /users: AuthGuard wraps AdminOrStaffGuard wraps UsersPage
 * - /no-access: AuthGuard wraps NoAccessPage
 * - Other routes have no guards
 *
 * @example
 * ```tsx
 * <BrowserRouter>
 *   <AppRouter />
 * </BrowserRouter>
 * ```
 */
export function AppRouter() {
  return (
    <Routes>
      {/* Public landing page - no guard */}
      <Route path={ROUTES.LANDING} element={<LandingPage />} />

      {/* Users page - requires authentication AND (active-org admin OR staff
          admin). Staff admins manage other orgs' members via the org picker. */}
      <Route
        path={ROUTES.USERS}
        element={
          <AuthGuard>
            <AdminOrStaffGuard>
              <UsersPage />
            </AdminOrStaffGuard>
          </AuthGuard>
        }
      />

      {/* Profile page - requires authentication only (admin or not) */}
      <Route
        path={ROUTES.PROFILE}
        element={
          <AuthGuard>
            <ProfilePage />
          </AuthGuard>
        }
      />

      {/* No access page - requires authentication only */}
      <Route
        path={ROUTES.NO_ACCESS}
        element={
          <AuthGuard>
            <NoAccessPage />
          </AuthGuard>
        }
      />

      {/* Error page - no guard, accessible in any state */}
      <Route path={ROUTES.ERROR} element={<ErrorPage />} />

      {/* Catch-all - redirect to landing page */}
      <Route path="*" element={<Navigate to={ROUTES.LANDING} replace />} />
    </Routes>
  );
}
